import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { DashboardSection } from '@/components/dashboard/DashboardSection';
import { IntelligentInbox } from '@/components/inbox/IntelligentInbox';
import { Badge } from '@/components/ui/badge';
import { useCommunications } from '@/context/CommunicationsContext';
import { useDashboardSearch } from '@/hooks/useDashboardSearch';
import { isCriticalAlert } from '@/utils/escalation';
import { COMMUNICATION_CATEGORIES, } from '@/types';
function parseValidation(v) {
    if (v === 'pending' || v === 'validated' || v === 'escalated')
        return v;
    return 'all';
}
function parseCategory(v) {
    if (v && COMMUNICATION_CATEGORIES.includes(v)) {
        return v;
    }
    return 'all';
}
function parsePriority(v) {
    if (v === 'low' || v === 'medium' || v === 'high' || v === 'critical')
        return v;
    return 'all';
}
const pageContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.07, delayChildren: 0.04 },
    },
};
const pageItem = {
    hidden: { opacity: 0, y: 12 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
    },
};
export function InboxPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { communications, error } = useCommunications();
    const { search, setSearch } = useDashboardSearch();
    // All filters live in the URL so dashboard/analytics tiles can deep-link
    // and views stay shareable.
    const validationFilter = parseValidation(searchParams.get('validation'));
    const categoryFilter = parseCategory(searchParams.get('category'));
    const priorityFilter = parsePriority(searchParams.get('priority'));
    const criticalOnly = searchParams.get('alert') === 'critical';
    const replyFilter = searchParams.get('reply') === 'yes';
    const setParam = useCallback((key, value) => {
        setSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            if (value === null || value === 'all')
                p.delete(key);
            else
                p.set(key, value);
            return p;
        }, { replace: true });
    }, [setSearchParams]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return communications.filter((c) => {
            if (categoryFilter !== 'all' &&
                c.category !== categoryFilter &&
                !(c.categories || []).includes(categoryFilter)) {
                return false;
            }
            if (validationFilter !== 'all' && c.validationStatus !== validationFilter) {
                return false;
            }
            if (priorityFilter !== 'all' &&
                (c.priority || c.urgencyStatus) !== priorityFilter) {
                return false;
            }
            if (criticalOnly && !isCriticalAlert(c))
                return false;
            if (replyFilter && !c.requiresReply)
                return false;
            if (!q)
                return true;
            return (c.subject.toLowerCase().includes(q) ||
                c.bodyPreview.toLowerCase().includes(q) ||
                c.sender.address.toLowerCase().includes(q) ||
                c.sender.name.toLowerCase().includes(q));
        });
    }, [
        communications,
        search,
        categoryFilter,
        validationFilter,
        priorityFilter,
        criticalOnly,
        replyFilter,
    ]);
    const specialChips = useMemo(() => {
        const chips = [];
        if (criticalOnly)
            chips.push({ key: 'alert', label: 'Critical alerts' });
        if (replyFilter)
            chips.push({ key: 'reply', label: 'Needs reply' });
        return chips;
    }, [criticalOnly, replyFilter]);
    const listAside = useMemo(() => (_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [specialChips.map((chip) => (_jsxs("button", { type: "button", onClick: () => setParam(chip.key, null), className: "inline-flex items-center gap-1 rounded-md bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-800 transition-colors hover:bg-rose-200 dark:bg-rose-500/20 dark:text-rose-200", title: "Clear this filter", children: [chip.label, _jsx(X, { className: "h-3 w-3", "aria-hidden": true })] }, chip.key))), _jsxs(Badge, { variant: "secondary", className: "rounded-md px-2.5 py-1 text-xs font-medium tabular-nums", children: [filtered.length, " shown"] }), _jsxs("span", { className: "hidden text-xs text-muted-foreground sm:inline", children: ["of ", communications.length, " loaded"] })] })), [specialChips, setParam, filtered.length, communications.length]);
    return (_jsxs(motion.div, { className: "mx-auto flex w-full max-w-[1400px] flex-col gap-10 pb-10", variants: pageContainer, initial: "hidden", animate: "show", children: [_jsx(motion.div, { variants: pageItem, children: _jsx(DashboardSection, { eyebrow: "Workspace", title: "Intelligent inbox", description: "Search the bar above or use the fields below, then open any row for the full message and AI review. Filters stay in the URL so you can share a view.", aside: listAside }) }), error ? (_jsx(motion.div, { variants: pageItem, role: "status", className: error.includes('0 messages')
                    ? 'rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 shadow-sm dark:text-amber-50'
                    : 'rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive shadow-sm', children: error })) : null, _jsx(motion.div, { variants: pageItem, className: "min-w-0", children: _jsx(IntelligentInbox, { items: filtered, search: search, onSearchChange: setSearch, categoryFilter: categoryFilter, onCategoryFilter: (v) => setParam('category', v), validationFilter: validationFilter, onValidationFilter: (v) => setParam('validation', v), priorityFilter: priorityFilter, onPriorityFilter: (v) => setParam('priority', v), onSelect: (id) => navigate(`/inbox/${encodeURIComponent(id)}`), showSearchField: true }) })] }));
}
